/**
 * frontend/src/components/MappingWizardModal.ts
 * ===============================================
 * Wizard modal de 3 pasos para crear un perfil de mapeo de columnas.
 *
 * Paso 1 — Detectar:  llama POST /api/perfiles/detectar con el archivo subido.
 *                     Muestra columnas detectadas, formatos y advertencias, y
 *                     deja elegir cuál fila es el encabezado sobre una
 *                     previsualización del archivo. Esa elección va primero
 *                     porque de esa fila salen los nombres de columna que el
 *                     paso 2 va a ofrecer.
 * Paso 2 — Configurar: formulario pre-rellenado con las sugerencias. El usuario
 *                      puede corregir cualquier campo y verificar contra el
 *                      archivo real (POST /api/perfiles/validar).
 * Paso 3 — Guardar:   nombre + banco/ERP. Opción "Usar sin guardar" para perfil
 *                     temporal que no persiste.
 *
 * De dónde salen los números
 * --------------------------
 * El paso 1 solo puede hablar de filas crudas: cuenta lo que hay debajo del
 * encabezado, antes de aplicar mapeo alguno, y estima el período con el formato
 * de fecha que la heurística supuso. El paso 3 muestra lo que devolvió
 * /validar — movimientos reconocidos con el mapeo ya configurado y el período
 * real de esos movimientos. Son cifras distintas a propósito, y cada paso dice
 * cuál está mostrando: la que el usuario ve al guardar tiene que ser la que va
 * a producir la conciliación.
 *
 * Callbacks:
 *   onSave(profile)   → perfil guardado en la API y retornado al padre
 *   onUseOnce(draft)  → perfil temporal (no se llama a la API)
 *   onCancel()        → modal cerrado sin acción
 */

import { detectarColumnas, guardarPerfil, validarPerfil, ApiError } from "../api";
import type { ValidarPerfilResponse } from "../api";
import type {
  ColumnProfile,
  ColumnProfileResponse,
  FormatoFecha,
  FormatoNumerico,
  ResultadoDeteccion,
  TipoFuente,
  TipoImporte,
} from "../types";

import { escapeHtml as _escHtml } from "../utils/texto";
import { iconoAyuda, prepararDialogo } from "../utils/ui";
import { icon } from "../utils/icons";

// ── Etiquetas legibles para los enums ─────────────────────────────────────────

const FORMATO_NUMERICO_LABELS: Record<string, string> = {
  punto_miles_coma_dec: "Argentino / Europeo  (1.500,75)",
  coma_miles_punto_dec: "EE.UU. / UK  (1,500.75)",
  sin_sep_coma_dec:     "Sin miles, decimal coma  (1500,75)",
  sin_sep_punto_dec:    "Sin miles, decimal punto  (1500.75)",
};

// El orden es el del desplegable: primero los día-primero, que son los del
// mercado local, y después los mes-primero. Las claves deben coincidir con el
// enum FormatoFecha del backend.
const FORMATO_FECHA_LABELS: Record<FormatoFecha, string> = {
  dmy_slash:      "DD/MM/YYYY",
  dmy_dash:       "DD-MM-YYYY",
  dmy_dot:        "DD.MM.YYYY",
  dmy_short:      "DD/MM/YY",
  dmy_dash_short: "DD-MM-YY",
  ymd_iso:        "YYYY-MM-DD  (ISO 8601)",
  mdy_slash:      "MM/DD/YYYY  (EE.UU.)",
  mdy_dash_short: "MM-DD-YY  (EE.UU.)",
  excel_serial:   "Número Excel (serial)",
};

// ── Interfaz pública ──────────────────────────────────────────────────────────

interface WizardOptions {
  archivo: File;
  tipoFuente: TipoFuente;
  onSave:    (p: ColumnProfileResponse) => void;
  onUseOnce: (p: Partial<ColumnProfile>) => void;
  onCancel:  () => void;
  /**
   * Perfil a editar. Si viene, el wizard arranca con ese mapeo cargado y al
   * guardar actualiza ese mismo perfil (upsert por id) en vez de crear otro.
   *
   * La edición pasa por el flujo completo —incluida la verificación contra un
   * archivo real— a propósito: un perfil se puede romper con un solo cambio
   * de columna, y guardarlo sin comprobar produce conciliaciones plausibles y
   * equivocadas. Por eso editar exige tener el archivo cargado.
   */
  perfilExistente?: ColumnProfileResponse;
}

// ── Wizard ────────────────────────────────────────────────────────────────────


export class MappingWizardModal {
  private _overlay: HTMLElement;
  private _paso = 1;
  private _activePreviewColumn: number | null = null;
  private _deteccion: ResultadoDeteccion | null = null;
  private _draft: Partial<ColumnProfile> = {};
  private _hojaSeleccionada: string | null = null;
  private _opts: WizardOptions;
  /**
   * Resultado de la última verificación contra el archivo, o null si el mapeo
   * cambió desde entonces.
   *
   * Se guarda entero —y no solo un booleano— porque el paso 3 muestra estos
   * números: movimientos reconocidos y período real. Antes mostraba los de la
   * detección, que cuenta filas crudas antes de aplicar mapeo alguno; en un
   * libro mayor filtrado por cuenta eso daba un número que la conciliación
   * después no producía.
   */
  private _validacion: ValidarPerfilResponse | null = null;

  /**
   * El mapeo actual se verificó contra el archivo y devolvió datos.
   *
   * Se exige antes de continuar: un perfil que no logra leer una sola fila no
   * sirve, y descubrirlo recién al conciliar es tarde. Derivado de
   * `_validacion` en vez de guardado aparte: dos campos que dicen lo mismo
   * terminan discrepando, y acá el que discrepa habilita guardar un perfil con
   * los números de otro mapeo.
   */
  private get _verificado(): boolean {
    return this._validacion !== null;
  }

  /** true cuando el wizard está editando un perfil ya guardado. */
  private get _esEdicion(): boolean {
    return this._opts.perfilExistente !== undefined;
  }

  constructor(opts: WizardOptions) {
    this._opts = opts;
    this._draft = opts.perfilExistente
      ? { ...opts.perfilExistente.detalle }
      : { tipo_fuente: opts.tipoFuente, encoding: "utf-8", fila_inicio_datos: 1, valor_credito: "C" };
    this._overlay = this._crearOverlay();
    document.body.appendChild(this._overlay);
    this._render();
    prepararDialogo(this._overlay, () => { this.destroy(); this._opts.onCancel(); });
    this._detectar();
  }

  destroy(): void {
    this._overlay.remove();
  }

  // ── Overlay / shell ───────────────────────────────────────────────────────

  private _crearOverlay(): HTMLElement {
    const el = document.createElement("div");
    el.className = "wizard-overlay";
    el.addEventListener("click", (e) => {
      if (e.target === el) { this.destroy(); this._opts.onCancel(); }
    });
    return el;
  }

  private _render(): void {
    const teniaFoco = this._overlay.contains(document.activeElement);
    this._activePreviewColumn = null;
    this._overlay.innerHTML = `
      <div class="wizard-modal ${this._paso <= 2 ? "wizard-modal--ancho" : ""}" data-tipo-fuente="${this._opts.tipoFuente}" role="dialog" aria-modal="true" aria-labelledby="wiz-title">
        <div class="wizard-modal__identity"><span class="wizard-modal__identity-icon">${icon("mapping")}</span><div><p class="eyebrow">PERFIL DE LECTURA</p><h2 id="wiz-title">${this._esEdicion ? "Editar" : "Configurar"} mapeo de columnas</h2></div><span class="wizard-modal__source">${this._opts.tipoFuente === "extracto" ? "Extracto bancario" : "Libro mayor"}</span></div>
        <div class="wizard-modal__header">
          <div class="wizard-steps">
            ${[1,2,3].map(n => `
              <div class="wizard-step ${n === this._paso ? "active" : n < this._paso ? "done" : ""}" ${n === this._paso ? 'aria-current="step"' : ""}>
                <span class="wizard-step__num">${n < this._paso ? icon("check") : icon(n === 1 ? "detect" : n === 2 ? "settings" : "save")}</span>
                <span class="wizard-step__label">${
                  (this._esEdicion
                    ? ["Detectar", "Configurar", "Actualizar"]
                    : ["Detectar", "Configurar", "Guardar"])[n - 1]
                }</span>
              </div>
              ${n < 3 ? '<div class="wizard-step__line"></div>' : ""}
            `).join("")}
          </div>
          <button class="wizard-modal__close" id="wiz-close" type="button" aria-label="Cerrar mapeo">${icon("close")}</button>
        </div>
        <div class="wizard-modal__body" id="wiz-body" tabindex="-1"></div>
        <div class="wizard-modal__footer" id="wiz-footer"></div>
        <!-- Los cambios de paso y el resultado de la verificación son visuales:
             el indicador de arriba cambia de color y el panel aparece. Un lector
             de pantalla no anuncia ninguna de las dos cosas, así que lo que pasó
             se escribe también acá. -->
        <p class="solo-lectores" id="wiz-anuncio" role="status" aria-live="polite"></p>
      </div>`;

    this._overlay.querySelector("#wiz-close")!.addEventListener("click", () => { this.destroy(); this._opts.onCancel(); });
    this._renderPaso();
    if (teniaFoco) this._overlay.querySelector<HTMLElement>("#wiz-body")?.focus();
  }

  /**
   * Escribe en la región viva del modal.
   *
   * Se limpia antes de escribir porque un lector de pantalla no vuelve a
   * anunciar un texto idéntico al que ya está: sin el paso intermedio, dos
   * verificaciones seguidas con el mismo resultado se anuncian una sola vez.
   */
  private _anunciar(texto: string): void {
    const region = this._overlay.querySelector<HTMLElement>("#wiz-anuncio");
    if (!region) return;
    region.textContent = "";
    window.setTimeout(() => { region.textContent = texto; }, 60);
  }

  private _renderPaso(): void {
    const body   = this._overlay.querySelector<HTMLElement>("#wiz-body")!;
    const footer = this._overlay.querySelector<HTMLElement>("#wiz-footer")!;

    body.innerHTML   = "";
    footer.innerHTML = "";

    const titulos = this._esEdicion
      ? ["Detectar", "Configurar", "Actualizar"]
      : ["Detectar", "Configurar", "Guardar"];
    this._anunciar(`Paso ${this._paso} de 3: ${titulos[this._paso - 1]}`);

    // Paso 1 solo se renderiza cuando _deteccion ya llegó; mientras tanto
    // _detectar() maneja el contenido del body (spinner → resultado).
    if (this._paso === 1 && this._deteccion) this._renderPaso1(body, footer);
    if (this._paso === 2) this._renderPaso2(body, footer);
    if (this._paso === 3) this._renderPaso3(body, footer);
  }

  // ── PASO 1: Detectar ──────────────────────────────────────────────────────

  /**
   * Analiza el archivo y pre-rellena el borrador con las sugerencias.
   *
   * @param hoja            hoja del XLSX a analizar.
   * @param filaEncabezado  fila a usar como encabezado. Al indicarla, la
   *   detección la respeta y los desplegables se repueblan con las columnas de
   *   esa fila — es la salida cuando la heurística eligió mal.
   */
  private async _detectar(hoja?: string, filaEncabezado?: number): Promise<void> {
    const body = this._overlay.querySelector<HTMLElement>("#wiz-body")!;
    body.innerHTML = `
      <div class="wiz-loading">
        <div class="wiz-spinner"></div>
        <p>Leyendo la estructura de <strong>${_escHtml(this._opts.archivo.name)}</strong>${hoja ? ` — hoja <em>${_escHtml(hoja)}</em>` : ""}…</p>
      </div>`;

    try {
      const resp = await detectarColumnas(this._opts.archivo, hoja, filaEncabezado);
      this._deteccion = resp.deteccion;

      // Al editar, la detección solo aporta las columnas disponibles del
      // archivo: el mapeo que se muestra es el que el usuario guardó. Pisarlo
      // con las sugerencias convertiría "editar" en "empezar de cero", y el
      // usuario perdería sin aviso los ajustes finos que hizo la primera vez.
      // La excepción es cuando él mismo cambia la fila de encabezado: ahí pidió
      // re-analizar, y las columnas viejas ya no existen.
      if (this._esEdicion && filaEncabezado === undefined) {
        this._renderPaso();
        return;
      }

      // Pre-rellenar draft con las sugerencias
      const s = resp.perfil_sugerido;
      this._draft = {
        ...this._draft,
        col_fecha:        s.col_fecha        ?? undefined,
        col_descripcion:  s.col_descripcion  ?? undefined,
        tipo_importe:     (s.tipo_importe as TipoImporte) ?? "debe_haber",
        col_monto:        s.col_monto        ?? undefined,
        col_debe:         s.col_debe         ?? undefined,
        col_haber:        s.col_haber        ?? undefined,
        col_saldo:        s.col_saldo        ?? undefined,
        col_referencia:   s.col_referencia   ?? undefined,
        // Sin ninguna columna que se parezca a una cuenta, lo probable es que
        // el sistema contable ya haya exportado solo la cuenta a conciliar. Se
        // deja marcado, a la vista y editable: es una sugerencia, no una
        // decisión tomada por el usuario.
        cuenta_unica:     this._draft.tipo_fuente === "libro_mayor" && !resp.deteccion.col_cuenta?.columna,
        col_cuenta:       undefined,
        codigo_cuenta:    undefined,
        formato_numerico: (s.formato_numerico as FormatoNumerico) ?? "punto_miles_coma_dec",
        formato_fecha:    (s.formato_fecha as FormatoFecha) ?? "dmy_slash",
        fila_inicio_datos: resp.deteccion.fila_inicio_datos ?? 0,
      };
      this._renderPaso();
    } catch (err) {
      // Con un mensaje del servidor, ese mensaje ya explica el problema y qué
      // hacer: agregarle debajo "el archivo tiene que ser XLSX, XLS o CSV" lo
      // contradice cuando el archivo sí tiene una de esas extensiones. La
      // línea genérica queda solo para el caso sin explicación.
      //
      // El mensaje se escapa: puede incluir el nombre del archivo, que lo
      // elige el usuario.
      const mensaje = err instanceof ApiError ? err.message : null;
      body.innerHTML = `
        <div class="wiz-error">
          <p class="wiz-error__icon">${icon("alert")}</p>
          <p class="wiz-error__msg">${_escHtml(mensaje ?? "No fue posible analizar el archivo.")}</p>
          ${mensaje ? "" : `<p class="wiz-error__hint">Los formatos admitidos son XLSX, XLS y CSV.</p>`}
        </div>`;
      const footer = this._overlay.querySelector<HTMLElement>("#wiz-footer")!;
      footer.innerHTML = `<button class="btn btn--ghost" id="wiz-cancel">Cancelar</button>`;
      footer.querySelector("#wiz-cancel")!.addEventListener("click", () => { this.destroy(); this._opts.onCancel(); });
    }
  }

  /**
   * Tabla con las primeras filas del archivo para elegir cuál es el encabezado.
   *
   * Se decide acá, antes del mapeo, porque de esa fila salen los nombres de
   * columna que después se ofrecen. Elegirla viendo el archivo es mucho más
   * fiable que escribir un número: el usuario reconoce su propio extracto de
   * un vistazo.
   */
  private _renderSelectorEncabezado(
    d: ResultadoDeteccion,
    modo: "elegir-fila" | "solo-lectura" = "elegir-fila",
  ): string {
    const todas = d.filas_preview ?? [];
    if (todas.length === 0) return "";

    const eligeFila = modo === "elegir-fila";
    const desde = eligeFila ? 0 : (d.fila_inicio_datos ?? 0);

    // En el paso 2 la tabla arranca en el encabezado. Lo que hay arriba es
    // otra cosa —Mercado Pago abre con un bloque de totales, los bancos con
    // metadata de la cuenta— y no participa del mapeo: mostrarlo agrega ruido
    // y, peor, invita a leer sus columnas como si fueran las que se están
    // configurando.
    const filas = todas.slice(desde);

    const anchoMax = Math.max(...filas.map(f => f.length));

    // `data-col` en cada celda: es lo que permite resaltar una columna entera
    // desde el paso 2 sin volver a recorrer la tabla buscando por texto.
    const celdas = (fila: string[]) =>
      Array.from(
        { length: anchoMax },
        (_, i) => `<td data-col="${i}" title="${_escHtml(fila[i] ?? "")}">${_escHtml(fila[i] ?? "")}</td>`,
      ).join("");

    const encabezado = eligeFila
      ? `<p class="wiz-encabezado__label">
          Elija la fila del encabezado
          <span class="wiz-encabezado__hint">
            — haga clic en la fila que contiene los nombres de las columnas
          </span>
        </p>`
      : `<p class="wiz-encabezado__label">
          Su archivo
          <span class="wiz-encabezado__hint">
            — al elegir una columna se resalta aquí
          </span>
        </p>`;

    // En solo lectura las filas no son botones: sin `role` ni `tabindex` no
    // entran en el orden de tabulación, que en el paso 2 tiene que llevar de
    // un selector al siguiente y no pasar por doce filas de una tabla.
    const attrsFila = (i: number) => eligeFila
      ? `data-fila="${i}" role="button" tabindex="0" title="Usar la fila ${i + 1} como encabezado"`
      : `data-fila="${i}"`;

    return `
      <div class="wiz-encabezado ${eligeFila ? "" : "wiz-encabezado--lectura"}">
        ${encabezado}
        <div class="wiz-encabezado__tabla-wrap">
          <table class="wiz-encabezado__tabla">
            <tbody>
              ${filas.map((fila, i) => {
                const n = desde + i;   // número de fila en el archivo
                return `
                <tr class="wiz-encabezado__fila ${n === d.fila_inicio_datos ? "is-selected" : ""}"
                    ${attrsFila(n)}>
                  <td class="wiz-encabezado__num">${n + 1}</td>
                  ${celdas(fila)}
                </tr>`;
              }).join("")}
            </tbody>
          </table>
        </div>
        ${eligeFila ? `<p class="wiz-encabezado__selection" role="status">Fila seleccionada: <strong>${d.fila_inicio_datos + 1}</strong></p>` : ""}
      </div>`;
  }

  /**
   * Resalta en la vista previa la columna que el usuario está mapeando.
   *
   * Es lo que hace que el paso 2 deje de pedir memoria: el selector dice
   * "Fecha" y abajo se ve cuál columna es, con su contenido real. Sin esto el
   * usuario tiene que abrir el Excel en otra ventana para recordar qué había
   * en cada columna, que es exactamente lo que el wizard vino a evitar.
   *
   * El índice sale del encabezado de la previsualización, no de
   * `columnas_disponibles`: esa lista viene filtrada —descarta las columnas
   * sin nombre— y sus posiciones no coinciden con las de la tabla.
   */
  private _resaltarColumna(nombre: string | null): void {
    const filas = this._deteccion?.filas_preview ?? [];
    const cabecera = filas[this._deteccion?.fila_inicio_datos ?? 0] ?? [];
    const idx = nombre ? cabecera.findIndex(c => String(c ?? "").trim() === nombre) : -1;
    this._activePreviewColumn = idx >= 0 ? idx : null;
    const preview = this._overlay.querySelector<HTMLElement>(".wiz-encabezado--lectura");
    if (!preview) return;

    preview.querySelectorAll(".is-col-resaltada")
      .forEach(td => td.classList.remove("is-col-resaltada"));

    if (this._activePreviewColumn === null) return;

    // Sin filtrar por fila: la tabla del paso 2 ya arranca en el encabezado,
    // así que todo lo que se ve pertenece a la tabla que se está mapeando.
    const celdas = preview.querySelectorAll<HTMLElement>(
      `tr[data-fila] td[data-col="${this._activePreviewColumn}"]`,
    );
    celdas.forEach(td => td.classList.add("is-col-resaltada"));

    if (celdas[0]) this._traerColumnaALaVista(preview, celdas[0]);
  }

  /**
   * Desplaza la previsualización hasta dejar visible la columna resaltada.
   *
   * Sin esto, resaltar una columna que está fuera de cuadro no sirve de nada:
   * el usuario tiene que buscarla a mano moviendo la tabla de costado, y
   * después volver al selector. Un extracto con ocho columnas no entra en el
   * panel, así que es la mayoría de los casos.
   *
   * Se mueve solo `scrollLeft` de la tabla: usar `scrollIntoView` arrastraría
   * también el cuerpo del modal y el formulario se iría de lugar justo cuando
   * el usuario está operando un selector.
   */
  private _traerColumnaALaVista(preview: HTMLElement, celda: HTMLElement): void {
    const wrap = preview.querySelector<HTMLElement>(".wiz-encabezado__tabla-wrap");
    if (!wrap) return;

    // La columna de números está anclada a la izquierda y tapa lo que quede
    // debajo: el borde útil empieza después de ella.
    const ancla = celda.closest("tr")?.querySelector<HTMLElement>(".wiz-encabezado__num");
    const borde = ancla?.offsetWidth ?? 0;
    const margen = 12;

    const izquierda = celda.offsetLeft;
    const derecha   = izquierda + celda.offsetWidth;

    // Asignación directa y no `scrollTo({behavior:"smooth"})`: el desplazamiento
    // suave se anima junto con el render y no ocurre si la pestaña está en
    // segundo plano, así que la columna podía quedar fuera de cuadro. Además,
    // para un salto que dispara al enfocar un campo, instantáneo se lee mejor
    // que animado: el usuario ya está mirando la tabla cuando llega.
    if (izquierda < wrap.scrollLeft + borde + margen) {
      wrap.scrollLeft = Math.max(0, izquierda - borde - margen);
    } else if (derecha > wrap.scrollLeft + wrap.clientWidth - margen) {
      wrap.scrollLeft = derecha - wrap.clientWidth + margen;
    }
  }

  private _renderPaso1(body: HTMLElement, footer: HTMLElement): void {
    const d = this._deteccion!;

    const periodo = d.fecha_min && d.fecha_max
      ? `${d.fecha_min} → ${d.fecha_max}`
      : "No detectado";

    const filas = d.total_filas > 0 ? `${d.total_filas}` : "Conteo pendiente";

    const fmt_num = d.formato_numerico
      ? FORMATO_NUMERICO_LABELS[d.formato_numerico] ?? d.formato_numerico
      : "No detectado";

    const fmt_fec = d.formato_fecha
      ? FORMATO_FECHA_LABELS[d.formato_fecha] ?? d.formato_fecha
      : "No detectado";

    const confianzaColor = (c: number) =>
      c >= 0.8 ? "high" : c >= 0.5 ? "mid" : "low";

    const chipCols = [
      { label: "Fecha",        col: d.col_fecha       },
      { label: "Descripción",  col: d.col_descripcion },
      { label: "Crédito",      col: d.col_debe        },
      { label: "Débito",       col: d.col_haber       },
      { label: "Monto",        col: d.col_monto       },
      { label: "Saldo",        col: d.col_saldo       },
    ].filter(c => c.col.columna);

    body.innerHTML = `
      <div class="wiz-detect">
        <div class="wiz-detect__overview">
        <h3 class="wiz-detect__title">Estructura detectada</h3>
        <p class="wiz-detect__file">${icon("file")} ${_escHtml(this._opts.archivo.name)}</p>

        <div class="wiz-detect__meta">
          <div class="wiz-meta-item"><div><strong>Período estimado ${iconoAyuda(`
            Calculado con la columna y el formato de fecha que la detección
            <b>supuso</b>, antes de que usted los confirme.
            <br><br>
            El período definitivo se muestra en el paso 3, con su mapeo
            aplicado.`)}</strong><span>${periodo}</span></div></div>
          <div class="wiz-meta-item"><div><strong>Filas bajo el encabezado ${iconoAyuda(`
            Cuántas filas hay debajo del encabezado, <b>sin aplicar el mapeo</b>.
            <br><br>
            No es la cantidad de movimientos: todavía no se descartaron las
            filas sin fecha ni se filtró por código de cuenta. Ese número
            aparece al verificar, en el paso 2.`)}</strong><span>${filas}</span></div></div>
          <div class="wiz-meta-item"><div><strong>Formato numérico</strong><span>${fmt_num}</span></div></div>
          <div class="wiz-meta-item"><div><strong>Formato fechas</strong><span>${fmt_fec}</span></div></div>
        </div>

        ${chipCols.length > 0 ? `
        <div class="wiz-detect__chips-label">Columnas reconocidas:</div>
        <div class="wiz-detect__chips">
          ${chipCols.map(c => `
            <span class="wiz-chip wiz-chip--${confianzaColor(c.col.confianza)}">
              ${c.label}: <strong>${_escHtml(c.col.columna!)}</strong>
            </span>`).join("")}
        </div>` : ""}

        ${d.hojas_disponibles.length > 1 ? `
        <div class="wiz-detect__hojas">
          <label>Hoja a usar:
            <select id="wiz-hoja-select">
              ${d.hojas_disponibles.map(h => `<option value="${_escHtml(h)}" ${h === (this._hojaSeleccionada ?? d.hojas_disponibles[0]) ? "selected" : ""}>${_escHtml(h)}</option>`).join("")}
            </select>
          </label>
        </div>` : ""}

        ${d.advertencias.length > 0 ? `
        <div class="wiz-detect__warnings">
          ${d.advertencias.map(w => `<p class="wiz-warning">${icon("alert")} ${_escHtml(w)}</p>`).join("")}
        </div>` : ""}
        </div>
        <div class="wiz-detect__workspace">${this._renderSelectorEncabezado(d)}</div>
      </div>`;

    footer.innerHTML = `
      <button class="btn btn--ghost" id="wiz-cancel">Cancelar</button>
      <button class="btn btn--primary" id="wiz-next">Configurar mapeo ${icon("arrow")}</button>`;

    footer.querySelector("#wiz-cancel")!.addEventListener("click", () => { this.destroy(); this._opts.onCancel(); });
    footer.querySelector("#wiz-next")!.addEventListener("click", () => {
      this._paso = 2;
      this._render();
    });

    // Multi-sheet: re-detectar cuando el usuario cambia de hoja
    body.querySelector<HTMLSelectElement>("#wiz-hoja-select")?.addEventListener("change", (e) => {
      const hoja = (e.target as HTMLSelectElement).value;
      this._hojaSeleccionada = hoja;
      this._draft.hoja_excel = hoja;
      this._deteccion = null;
      this._render();
      this._detectar(hoja);
    });

    // Elegir otra fila de encabezado re-analiza el archivo desde ahí: cambian
    // las columnas disponibles y, con ellas, todas las sugerencias de mapeo.
    body.querySelectorAll<HTMLElement>(".wiz-encabezado__fila").forEach(fila => {
      const seleccionar = () => {
        const indice = Number(fila.dataset.fila);
        if (!Number.isFinite(indice) || indice === this._deteccion?.fila_inicio_datos) return;

        this._deteccion = null;
        this._render();
        void this._detectar(this._hojaSeleccionada ?? undefined, indice);
      };

      fila.addEventListener("click", seleccionar);
      fila.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          seleccionar();
        }
      });
    });
  }

  // ── PASO 2: Configurar ────────────────────────────────────────────────────

  private _renderPaso2(body: HTMLElement, footer: HTMLElement): void {
    const d    = this._deteccion!;
    const dr   = this._draft;
    const cols = d.columnas_disponibles;

    const selectOpts = (selected?: string | null) =>
      `<option value="">— ninguna —</option>` +
      cols.map(c => `<option value="${_escHtml(c)}" ${c === selected ? "selected" : ""}>${_escHtml(c)}</option>`).join("");

    const tipoImporte = dr.tipo_importe ?? "debe_haber";

    body.innerHTML = `
      <div class="wiz-paso2">
      <div class="wiz-form">
        <!-- Columnas -->
        <div class="wiz-form__section wiz-form__section--rows" data-recorrido="columnas">
          <h4 class="wiz-form__section-title">Columnas</h4>

          <div class="wiz-form__row">
            <label for="wf-fecha">Fecha <span class="req">*</span></label>
            <select id="wf-fecha" data-col-sel>${selectOpts(dr.col_fecha)}</select>
          </div>
          <div class="wiz-form__row">
            <label for="wf-desc">Descripción <span class="req">*</span></label>
            <select id="wf-desc" data-col-sel>${selectOpts(dr.col_descripcion)}</select>
          </div>
          <div class="wiz-form__row">
            <label for="wf-saldo">Saldo <span class="req">*</span></label>
            <select id="wf-saldo" data-col-sel>${selectOpts(dr.col_saldo)}</select>
          </div>
          ${dr.tipo_fuente === "libro_mayor" ? `
          <div class="wiz-form__row">
            <label for="wf-ref">Asiento / Nro. operación <span class="req">*</span></label>
            <select id="wf-ref" data-col-sel>${selectOpts(dr.col_referencia)}</select>
          </div>
          <div class="wiz-form__row wiz-form__row--split">
            <label>Cuenta / Código <span class="req" ${dr.cuenta_unica ? "hidden" : ""}>*</span></label>
            <div class="wiz-form__row-split-inputs">
              <select id="wf-cuenta" data-col-sel title="Columna que contiene el código de cuenta" ${dr.cuenta_unica ? "disabled" : ""}>${selectOpts(dr.col_cuenta)}</select>
              <input type="text" id="wf-codigo-cuenta" placeholder="ej. 1.1.1.07" aria-label="Código de cuenta a filtrar"
              value="${_escHtml(dr.codigo_cuenta ?? "")}" title="Valor del código de cuenta a filtrar" ${dr.cuenta_unica ? "disabled" : ""} />
            </div>
          </div>
          <label class="wiz-form__casilla">
            <input type="checkbox" id="wf-cuenta-unica" ${dr.cuenta_unica ? "checked" : ""} />
            El archivo trae una sola cuenta
            ${iconoAyuda(`
              Márquelo si su sistema contable exportó solo el mayor de la cuenta
              a conciliar: no hace falta filtrar por código.
              <br><br>
              Si el archivo trae varias cuentas, desmárquelo e indique la columna
              y el código de la cuenta. Mezclarlas da un resultado que parece
              correcto y no lo es.`)}
          </label>` : ""}
        </div>

        <!-- Importe -->
        <div class="wiz-form__section wiz-form__section--rows" data-recorrido="importe">
          <h4 class="wiz-form__section-title">Columna(s) de importe <span class="req">*</span></h4>
          <div class="wiz-form__radio-group">
            <label><input type="radio" name="tipo_importe" value="debe_haber" ${tipoImporte === "debe_haber" ? "checked" : ""}> Crédito / Débito separados</label>
            <label><input type="radio" name="tipo_importe" value="columna_unica" ${tipoImporte === "columna_unica" ? "checked" : ""}> Una columna con signo (±)</label>
            <label><input type="radio" name="tipo_importe" value="monto_tipo" ${tipoImporte === "monto_tipo" ? "checked" : ""}> Monto + tipo (C / D)</label>
          </div>

          <div id="wf-importe-fields">
            ${this._renderImporteFields(tipoImporte, dr, selectOpts)}
          </div>
        </div>

        <!-- Matching. Va antes que Formatos porque siempre está presente: la
             sección de formatos se oculta entera cuando no aplica a este
             archivo, y una sección que aparece y desaparece queda mejor al
             final que en el medio. -->
        <div class="wiz-form__section wiz-form__section--rows" data-recorrido="comparacion">
          <h4 class="wiz-form__section-title">Comparación de movimientos</h4>
          <div class="wiz-form__row wiz-form__row--compacto">
            <label for="wf-tolerancia">
              Tolerancia de días
              ${iconoAyuda(`
                Corrimiento máximo entre la fecha del banco y la del asiento
                para considerarlos el mismo movimiento.
                <br><br>
                <b>0</b> — mismo día exacto<br>
                <b>1</b> — el banco acredita al día siguiente<br>
                <b>31</b> — el libro agrupa el mes en un asiento
                (ej: rendimientos diarios)
                <br><br>
                Si el extracto y el libro tienen valores distintos, rige el mayor.`)}
            </label>
            <input type="number" id="wf-tolerancia" min="0" max="31" step="1"
              value="${dr.tolerancia_dias ?? 1}" />
          </div>
        </div>

        ${this._renderSeccionFormatos(d, dr)}

        <!-- Resultado de validación -->
        <div id="wf-validar-result" hidden></div>

        <p id="wf-error" class="wiz-form__error" hidden></p>
      </div>

      <aside class="wiz-paso2__preview">
        ${this._renderSelectorEncabezado(d, "solo-lectura")}
      </aside>
      </div>`;

    // ── Resaltado de la columna que se está mapeando ──────────────────────
    //
    // Delegado en el contenedor y no enganchado a cada `select`: los campos de
    // importe se vuelven a renderizar cada vez que cambia el tipo, y unos
    // listeners por elemento quedarían colgados en los nodos viejos.
    const alFocoColumna = (e: Event) => {
      const el = e.target;
      if (el instanceof HTMLSelectElement && el.dataset.colSel !== undefined && document.activeElement === el) {
        this._resaltarColumna(el.value || null);
      }
    };
    body.addEventListener("focusin", alFocoColumna);
    body.addEventListener("change", alFocoColumna);
    body.addEventListener("focusout", (e) => {
      const el = e.target;
      if (el instanceof HTMLSelectElement && el.dataset.colSel !== undefined) {
        this._resaltarColumna(null);
      }
    });

    // ── Listeners ─────────────────────────────────────────────────────────
    body.querySelectorAll<HTMLInputElement>("input[name='tipo_importe']").forEach(radio => {
      radio.addEventListener("change", () => {
        const ti = radio.value as TipoImporte;
        this._draft.tipo_importe = ti;
        const fieldsEl = body.querySelector<HTMLElement>("#wf-importe-fields")!;
        fieldsEl.innerHTML = this._renderImporteFields(ti, this._draft, selectOpts);
        this._bindImporteListeners(body);
        this._invalidarVerificacion();
      });
    });
    this._bindImporteListeners(body);

    // Una sola cuenta reemplaza a cuenta + código: se vacían y se bloquean para
    // que el perfil no declare las dos cosas a la vez. Va antes que el listener
    // general, que lee el formulario ya actualizado.
    body.querySelector<HTMLInputElement>("#wf-cuenta-unica")?.addEventListener("change", (e) => {
      const unica = (e.target as HTMLInputElement).checked;
      body.querySelectorAll<HTMLSelectElement | HTMLInputElement>("#wf-cuenta, #wf-codigo-cuenta").forEach(campo => {
        campo.disabled = unica;
        if (unica) campo.value = "";
      });
      body.querySelector<HTMLElement>(".wiz-form__row--split .req")!.hidden = unica;
    });

    // Tocar cualquier campo del mapeo invalida la verificación anterior: lo que
    // se verificó ya no es lo que está configurado.
    //
    // Se escuchan `change` e `input`: en los campos de texto `change` recién
    // dispara al perder el foco, y el usuario que escribe un código de cuenta
    // no debería ver el botón bloqueado mientras tipea.
    body.querySelectorAll("select, input").forEach(campo => {
      const alEditar = () => {
        this._leerFormulario(body);
        this._invalidarVerificacion();
      };
      campo.addEventListener("change", alEditar);
      campo.addEventListener("input", alEditar);
    });

    // El motivo del bloqueo viaja en el envoltorio y no en el botón: un
    // `<button disabled>` no emite eventos de mouse, así que un tooltip puesto
    // sobre él no aparecería justo cuando hace falta.
    footer.innerHTML = `
      <p class="wizard-modal__action-hint" id="wiz-action-hint" role="status"></p>
      <button class="btn btn--ghost" id="wiz-back">${icon("back")} Atrás</button>
      <span class="wiz-tip-wrap" id="wiz-validar-tip">
        <button class="btn btn--secondary" id="wiz-validar">${icon("shield")} Verificar</button>
      </span>
      <span class="wiz-tip-wrap" id="wiz-next-tip">
        <button class="btn btn--primary" id="wiz-next" disabled>Continuar ${icon("arrow")}</button>
      </span>`;

    footer.querySelector("#wiz-back")!.addEventListener("click", () => {
      this._paso = 1; this._render();
    });

    footer.querySelector("#wiz-validar")!.addEventListener("click", () => {
      this._leerFormulario(body);
      void this._validarConBackend(body);
    });

    footer.querySelector("#wiz-next")!.addEventListener("click", () => {
      this._leerFormulario(body);
      const err = this._validarDraft();
      if (err) {
        const errEl = body.querySelector<HTMLElement>("#wf-error")!;
        errEl.textContent = err;
        errEl.hidden = false;
        return;
      }
      this._paso = 3; this._render();
    });

    // Estado inicial de los botones según lo que ya trae el borrador.
    this._actualizarAccionesPaso2();


  }

  /**
   * Campos obligatorios que todavía están sin completar.
   *
   * Cuáles son depende del tipo de importe elegido, así que la lista se arma
   * dinámicamente. Devuelve etiquetas legibles para poder decirle al usuario
   * qué le falta en lugar de solo bloquearle el botón.
   */
  private _camposFaltantes(): string[] {
    const d = this._draft;
    const faltantes: string[] = [];

    if (!d.col_fecha)       faltantes.push("Fecha");
    if (!d.col_descripcion) faltantes.push("Descripción");
    // El saldo no es decorativo: de él salen los saldos inicial y final, y es
    // la señal que revela si el archivo viene en orden cronológico inverso.
    if (!d.col_saldo)       faltantes.push("Saldo");

    if (d.tipo_fuente === "libro_mayor") {
      if (!d.col_referencia) faltantes.push("Asiento / Nro. operación");
      // Sin el código a filtrar, el libro aporta los movimientos de todas las
      // cuentas desplegadas mezclados contra un extracto de una sola: el
      // resultado sale plausible pero es incorrecto. Solo se omite si el
      // usuario declara que el archivo trae una sola cuenta.
      if (!d.cuenta_unica) {
        if (!d.col_cuenta)            faltantes.push("Cuenta / Código");
        if (!d.codigo_cuenta?.trim()) faltantes.push("Código de cuenta a conciliar");
      }
    }

    if (d.tipo_importe === "columna_unica" && !d.col_monto) {
      faltantes.push("Importe");
    }
    if (d.tipo_importe === "debe_haber") {
      if (!d.col_debe)  faltantes.push("Columna de ingresos");
      if (!d.col_haber) faltantes.push("Columna de egresos");
    }
    if (d.tipo_importe === "monto_tipo") {
      if (!d.col_monto_val)  faltantes.push("Columna del importe");
      if (!d.col_monto_tipo) faltantes.push("Columna del tipo (C/D)");
    }

    return faltantes;
  }

  /**
   * Sincroniza los botones del paso 2 con el estado del formulario.
   *
   * Verificar exige el mapeo mínimo completo; Continuar exige además que esa
   * verificación haya dado bien. El aviso explica cuál de las dos condiciones
   * falta, para que el botón deshabilitado nunca sea un misterio.
   */
  private _actualizarAccionesPaso2(): void {
    const btnVal  = this._overlay.querySelector<HTMLButtonElement>("#wiz-validar");
    const btnNext = this._overlay.querySelector<HTMLButtonElement>("#wiz-next");
    if (!btnVal || !btnNext) return;

    const faltantes = this._camposFaltantes();
    btnVal.disabled  = faltantes.length > 0;
    btnNext.disabled = !this._verificado;
    const hint = this._overlay.querySelector<HTMLElement>("#wiz-action-hint");
    if (hint) hint.textContent = faltantes.length
      ? `Complete los campos para poder verificar: ${faltantes.join(", ")}.`
      // Verificado, el bloque de resultado ya lo dice: el pie no lo repite.
      : this._verificado ? "" : "Verifique el mapeo para continuar.";

    // El motivo del bloqueo se lee pasando el mouse por el botón. Antes vivía
    // en un párrafo al pie del formulario: el usuario tenía que bajar a
    // buscarlo, mirando el botón que no responde.
    this._motivoDelBloqueo(
      "#wiz-validar-tip",
      faltantes.length > 0
        ? `Complete los campos para poder verificar: ${faltantes.join(", ")}.`
        : null,
    );

    this._motivoDelBloqueo(
      "#wiz-next-tip",
      this._verificado ? null : "Verifique el mapeo para continuar.",
    );
  }

  /** Pone o saca el motivo del bloqueo sobre el envoltorio de un botón. */
  private _motivoDelBloqueo(selector: string, motivo: string | null): void {
    const envoltorio = this._overlay.querySelector<HTMLElement>(selector);
    if (!envoltorio) return;

    if (motivo) {
      envoltorio.dataset.tooltip = motivo;
      envoltorio.tabIndex = 0;
      envoltorio.setAttribute("aria-label", motivo);
    } else {
      delete envoltorio.dataset.tooltip;
      envoltorio.removeAttribute("tabindex");
      envoltorio.removeAttribute("aria-label");
    }
  }

  /**
   * Trae a la vista el resultado de la verificación.
   *
   * Aparece debajo de los campos de formato, casi siempre fuera de cuadro: sin
   * esto el usuario apretaba "Verificar", no veía cambiar nada y tenía que
   * bajar a buscar la respuesta. Se mueve solo el cuerpo del modal, con
   * `smooth` para que se entienda de dónde a dónde fue — un salto seco se lee
   * como si la pantalla se hubiera roto.
   */
  private _traerResultadoALaVista(resultado: HTMLElement): void {
    const cuerpo = this._overlay.querySelector<HTMLElement>(".wizard-modal__body");
    if (!cuerpo) return;

    // Por rectángulos y no por `offsetTop`: el cuerpo del modal no es
    // `position: relative`, así que `offsetParent` es otro elemento y la cuenta
    // salía desplazada. Los rectángulos son relativos a la ventana y no
    // dependen de dónde esté el ancla.
    const margen = 16;
    const caja  = resultado.getBoundingClientRect();
    const marco = cuerpo.getBoundingClientRect();

    const sobresale = caja.bottom - marco.bottom;
    if (sobresale <= 0) return;   // ya se ve entero

    const destino = cuerpo.scrollTop + sobresale + margen;

    const suave = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    cuerpo.scrollTo({ top: destino, behavior: suave ? "smooth" : "auto" });
  }

  /** Invalida la verificación previa: el mapeo cambió después de verificarlo. */
  private _invalidarVerificacion(): void {
    this._validacion = null;

    // Limpiar el resultado anterior, no solo bloquear el botón: dejarlo a la
    // vista junto a un mapeo ya modificado hace creer que ese resultado
    // corresponde a la configuración actual.
    const resultado = this._overlay.querySelector<HTMLElement>("#wf-validar-result");
    if (resultado) {
      resultado.innerHTML = "";
      resultado.hidden = true;
    }

    const error = this._overlay.querySelector<HTMLElement>("#wf-error");
    if (error) {
      error.textContent = "";
      error.hidden = true;
    }

    this._actualizarAccionesPaso2();
  }

  /**
   * Sección "Formatos", o nada si ninguno de sus campos aplica al archivo.
   *
   * Los cuatro controles son condicionales: los formatos solo cuando el valor
   * llega como texto, el delimitador solo en CSV, la hoja solo en XLSX con
   * varias. Un XLSX de una hoja con tipos nativos no necesita ninguno, y
   * dejar el encabezado solo se vería como un bloque vacío.
   */
  private _renderSeccionFormatos(
    d: ResultadoDeteccion,
    dr: Partial<ColumnProfile>,
  ): string {
    const campos: string[] = [];

    if (this._formatoNumericoAplica()) {
      campos.push(`
        <div class="wiz-form__row">
          <label for="wf-fmt-num">Formato numérico</label>
          <select id="wf-fmt-num">
            ${Object.entries(FORMATO_NUMERICO_LABELS).map(([v, l]) =>
              `<option value="${v}" ${v === dr.formato_numerico ? "selected" : ""}>${l}</option>`
            ).join("")}
          </select>
        </div>`);
    }

    if (this._formatoFechaAplica()) {
      campos.push(`
        <div class="wiz-form__row">
          <label for="wf-fmt-fec">Formato de fechas</label>
          <select id="wf-fmt-fec">
            ${Object.entries(FORMATO_FECHA_LABELS).map(([v, l]) =>
              `<option value="${v}" ${v === dr.formato_fecha ? "selected" : ""}>${l}</option>`
            ).join("")}
          </select>
        </div>`);
    }

    if (/\.(csv|tsv)$/i.test(this._opts.archivo.name)) {
      campos.push(`
        <div class="wiz-form__row">
          <label for="wf-delim">Separador de campos (CSV)</label>
          <select id="wf-delim">
            <option value="" ${!dr.delimitador_csv ? "selected" : ""}>Auto-detectar</option>
            <option value="," ${dr.delimitador_csv === "," ? "selected" : ""}>Coma  ( , )</option>
            <option value=";" ${dr.delimitador_csv === ";" ? "selected" : ""}>Punto y coma  ( ; )</option>
            <option value="&#9;" ${dr.delimitador_csv === "\t" ? "selected" : ""}>Tabulador  ( ⇥ )</option>
            <option value="|" ${dr.delimitador_csv === "|" ? "selected" : ""}>Pipe  ( | )</option>
          </select>
        </div>`);
    }

    if (d.hojas_disponibles.length > 1) {
      campos.push(`
        <div class="wiz-form__row">
          <label for="wf-hoja">Hoja del XLSX</label>
          <select id="wf-hoja">
            ${d.hojas_disponibles.map(h =>
              `<option value="${h}" ${h === dr.hoja_excel ? "selected" : ""}>${h}</option>`
            ).join("")}
          </select>
        </div>`);
    }

    if (campos.length === 0) return "";

    return `
      <div class="wiz-form__section wiz-form__section--rows" data-recorrido="formatos">
        <h4 class="wiz-form__section-title">Formatos</h4>
        ${campos.join("")}
      </div>`;
  }

  /**
   * ¿El formato numérico cambia cómo se lee este archivo?
   *
   * Solo interviene cuando el importe llega como texto y hay que interpretarlo.
   * Un XLSX guarda los números con tipo propio y llegan ya convertidos: ahí
   * cualquier opción da el mismo resultado.
   */
  private _formatoNumericoAplica(): boolean {
    return this._deteccion?.formato_numerico_aplica ?? true;
  }

  /** Ídem para las fechas: un `datetime` de Excel no necesita formato. */
  private _formatoFechaAplica(): boolean {
    return this._deteccion?.formato_fecha_aplica ?? true;
  }

  /**
   * Resultado de una verificación exitosa: la primera fila tal como la leyó el
   * parser, más los avisos de formato si los hay.
   */
  private _renderVerificacionOk(resp: ValidarPerfilResponse): string {
    const LABEL: Record<string, string> = {
      fecha:       "Fecha",
      descripcion: "Descripción",
      asiento:     "Asiento / Nro. operación",
      cuenta:      "Cuenta / Código",
      credito:     "Crédito / Debe",
      debito:      "Débito / Haber",
      importe:     "Importe",
      saldo:       "Saldo",
    };

    const filas = Object.entries(resp.fila_muestra ?? {}).map(([clave, valor]) => `
      <tr>
        <td class="wiz-val__key">${_escHtml(LABEL[clave] ?? clave)}</td>
        <td class="wiz-val__val">${_escHtml(String(valor))}</td>
      </tr>`).join("");

    // Los avisos de coherencia informan y no bloquean —`coherencia.py` está
    // construido así a propósito—, por eso van discretos y no en el bloque
    // amarillo, que es el del fallo de verificación. Compartir el estilo hacía
    // que el usuario leyera "no puedo seguir" donde decía "confirma el mes".
    const avisos = (resp.advertencias ?? [])
      .map(a => `<p class="wiz-aviso">${_escHtml(a)}</p>`)
      .join("");

    return `
      <div class="wiz-validar-ok">
        <div class="verification-heading">${icon("shield")} <div><strong>Mapeo verificado</strong>${resp.total !== undefined ? `<span>${resp.total} movimientos reconocidos</span>` : ""}</div></div>
        ${resp.total === undefined ? `<p class="wiz-validar-ok__msg">${_escHtml(resp.mensaje)}</p>` : ""}
        <table class="wiz-val-table"><tbody>${filas}</tbody></table>
        ${avisos}
      </div>`;
  }

  private async _validarConBackend(body: HTMLElement): Promise<void> {
    const resultEl = body.querySelector<HTMLElement>("#wf-validar-result")!;
    const errEl    = body.querySelector<HTMLElement>("#wf-error")!;
    const btnVal   = this._overlay.querySelector<HTMLButtonElement>("#wiz-validar")!;

    errEl.hidden = true;
    resultEl.hidden = true;
    resultEl.innerHTML = "";
    btnVal.disabled = true;
    btnVal.textContent = "Validando…";

    // Mientras se re-verifica, lo anterior deja de valer.
    this._validacion = null;

    try {
      const resp = await validarPerfil(this._opts.archivo, this._draft);
      if (resp.ok && resp.fila_muestra) {
        // Solo una verificación con datos reales habilita el paso siguiente.
        this._validacion = resp;
        resultEl.innerHTML = this._renderVerificacionOk(resp);
      } else {
        resultEl.innerHTML = `<p class="wiz-validar-warn">${icon("alert")} ${_escHtml(resp.mensaje)}</p>`;
      }
      this._anunciar(resp.mensaje);
      resultEl.hidden = false;
      this._traerResultadoALaVista(resultEl);
    } catch (err) {
      const raw = err instanceof ApiError ? err.message : "No fue posible verificar el archivo. Revise las columnas seleccionadas.";
      // Simplificar mensajes técnicos del backend
      errEl.textContent = raw.replace(/No se pudo parsear el archivo con el perfil configurado:\s*/i, "");
      errEl.hidden = false;
      this._anunciar(errEl.textContent);
    } finally {
      btnVal.innerHTML = `${icon("shield")} Verificar`;
      // Recalcula el estado de ambos botones según el resultado recién obtenido.
      this._actualizarAccionesPaso2();
    }
  }

  private _renderImporteFields(ti: TipoImporte, dr: Partial<ColumnProfile>,
                                selectOpts: (s?: string | null) => string): string {
    if (ti === "columna_unica") return `
      <div class="wiz-form__row">
        <label for="wf-monto">Columna de monto <span class="req">*</span></label>
        <select id="wf-monto" data-col-sel>${selectOpts(dr.col_monto)}</select>
      </div>`;

    if (ti === "debe_haber") return `
      <div class="wiz-form__row">
        <label for="wf-debe">Columna de ingresos (Crédito / Debe) <span class="req">*</span></label>
        <select id="wf-debe" data-col-sel>${selectOpts(dr.col_debe)}</select>
      </div>
      <div class="wiz-form__row">
        <label for="wf-haber">Columna de egresos (Débito / Haber) <span class="req">*</span></label>
        <select id="wf-haber" data-col-sel>${selectOpts(dr.col_haber)}</select>
      </div>`;

    if (ti === "monto_tipo") return `
      <div class="wiz-form__row">
        <label for="wf-mv">Columna con el valor <span class="req">*</span></label>
        <select id="wf-mv" data-col-sel>${selectOpts(dr.col_monto_val)}</select>
      </div>
      <div class="wiz-form__row">
        <label for="wf-mt">Columna con el tipo (C/D) <span class="req">*</span></label>
        <select id="wf-mt" data-col-sel>${selectOpts(dr.col_monto_tipo)}</select>
      </div>
      <div class="wiz-form__row">
        <label for="wf-vc">Texto que indica Crédito</label>
        <input type="text" id="wf-vc" value="${_escHtml(dr.valor_credito ?? "C")}" placeholder="C, Crédito, CREDIT…" />
      </div>`;

    return "";
  }

  /**
   * Cablea los campos de importe, que se regeneran al cambiar el tipo.
   *
   * Cada cambio invalida la verificación previa: estos campos se recrean por
   * fuera del listener genérico del formulario, así que necesitan hacerlo por
   * su cuenta.
   */
  private _bindImporteListeners(body: HTMLElement): void {
    const _val = (id: string) => body.querySelector<HTMLSelectElement | HTMLInputElement>(id)?.value ?? null;

    const alCambiar = (
      selector: string,
      evento: "change" | "input",
      asignar: () => void,
    ) => {
      body.querySelector(selector)?.addEventListener(evento, () => {
        asignar();
        this._invalidarVerificacion();
      });
    };

    const ti = this._draft.tipo_importe;
    if (ti === "columna_unica") {
      alCambiar("#wf-monto", "change", () => { this._draft.col_monto = _val("#wf-monto") ?? undefined; });
    }
    if (ti === "debe_haber") {
      alCambiar("#wf-debe",  "change", () => { this._draft.col_debe  = _val("#wf-debe")  ?? undefined; });
      alCambiar("#wf-haber", "change", () => { this._draft.col_haber = _val("#wf-haber") ?? undefined; });
    }
    if (ti === "monto_tipo") {
      alCambiar("#wf-mv", "change", () => { this._draft.col_monto_val  = _val("#wf-mv") ?? undefined; });
      alCambiar("#wf-mt", "change", () => { this._draft.col_monto_tipo = _val("#wf-mt") ?? undefined; });
      alCambiar("#wf-vc", "input",  () => { this._draft.valor_credito  = _val("#wf-vc") ?? "C"; });
    }
  }

  private _leerFormulario(body: HTMLElement): void {
    const v = (id: string) => body.querySelector<HTMLSelectElement | HTMLInputElement>(id)?.value || undefined;
    this._draft.col_fecha        = v("#wf-fecha");
    this._draft.col_descripcion  = v("#wf-desc");
    this._draft.col_saldo        = v("#wf-saldo");
    this._draft.col_referencia   = v("#wf-ref");
    const unica = body.querySelector<HTMLInputElement>("#wf-cuenta-unica");
    if (unica) this._draft.cuenta_unica = unica.checked;
    this._draft.col_cuenta       = this._draft.cuenta_unica ? undefined : v("#wf-cuenta");
    this._draft.codigo_cuenta    = this._draft.cuenta_unica ? undefined : v("#wf-codigo-cuenta");
    // Los selectores de formato solo existen si aplican a este archivo. Cuando
    // no están, se conserva el valor detectado en vez de borrarlo: el perfil
    // igual necesita un formato válido aunque el parser no vaya a usarlo.
    this._draft.formato_numerico =
      (v("#wf-fmt-num") as FormatoNumerico | undefined) ?? this._draft.formato_numerico;
    this._draft.formato_fecha =
      (v("#wf-fmt-fec") as FormatoFecha | undefined) ?? this._draft.formato_fecha;
    // `fila_inicio_datos` no se lee acá: se eligió en el paso 1 y ya está en
    // el borrador. Volver a leerlo lo pisaría con un campo que ya no existe.
    this._draft.hoja_excel       = v("#wf-hoja");

    // Tolerancia: entero saneado en [0, 31]. El 0 es una elección válida
    // ("mismo día exacto"), así que solo el campo vacío cae al default.
    const tolRaw = parseInt(v("#wf-tolerancia") ?? "", 10);
    this._draft.tolerancia_dias =
      Number.isFinite(tolRaw) ? Math.min(31, Math.max(0, tolRaw)) : 1;
    const delimRaw = v("#wf-delim");
    this._draft.delimitador_csv  = delimRaw || null;  // "" → null (auto-detectar)

    const ti = this._draft.tipo_importe;
    if (ti === "columna_unica") this._draft.col_monto = v("#wf-monto");
    if (ti === "debe_haber")  { this._draft.col_debe = v("#wf-debe"); this._draft.col_haber = v("#wf-haber"); }
    if (ti === "monto_tipo")  { this._draft.col_monto_val = v("#wf-mv"); this._draft.col_monto_tipo = v("#wf-mt"); this._draft.valor_credito = v("#wf-vc") ?? "C"; }
  }

  /**
   * Mensaje de error si falta algún campo obligatorio, o null si está completo.
   *
   * Deriva de `_camposFaltantes` para que las reglas vivan en un solo lugar:
   * tener dos listas de campos obligatorios garantiza que tarde o temprano
   * discrepen.
   */
  private _validarDraft(): string | null {
    const faltantes = this._camposFaltantes();
    if (faltantes.length === 0) return null;

    return faltantes.length === 1
      ? `Falta completar: ${faltantes[0]}.`
      : `Faltan completar: ${faltantes.join(", ")}.`;
  }

  // ── PASO 3: Guardar ───────────────────────────────────────────────────────

  private _renderPaso3(body: HTMLElement, footer: HTMLElement): void {
    // Estos números salen de la verificación del paso 2, no de la detección
    // del paso 1: son los movimientos que el mapeo configurado reconoce de
    // verdad y el período que cubren. Si el usuario guarda un perfil creyendo
    // que procesa 1.240 filas y la conciliación después lee 87 movimientos,
    // el perfil se guardó con una expectativa falsa.
    //
    // A este paso no se llega sin verificar (`_verificado` habilita el botón),
    // así que `_validacion` está cargada. El `?` es por las dudas, no un
    // fallback: si alguna vez llegara vacío, mostrar "—" es honesto y mostrar
    // el conteo de la detección no lo sería.
    const v = this._validacion;
    const movimientos = v?.total !== undefined ? `${v.total}` : "—";
    const periodo = v?.rango_fechas_label || "—";

    body.innerHTML = `
      <div class="wiz-save">
        <div class="verification-heading">${icon("shield")} <div><strong>Mapeo verificado</strong><span>Guárdelo para reutilizarlo en su próxima conciliación.</span></div></div>
        <div class="wiz-save__summary">
          <div class="wiz-save__stat">${icon("ledger")}<span>${movimientos} movimientos reconocidos</span></div>
          <div class="wiz-save__stat">${icon("calendar")}<span>${_escHtml(periodo)}</span></div>
          <div class="wiz-save__stat">${icon("mapping")}<span>${FORMATO_NUMERICO_LABELS[this._draft.formato_numerico ?? ""] ?? "—"}</span></div>
        </div>

        <div class="wiz-form__row" data-recorrido="nombre-perfil">
          <label for="ws-nombre">Nombre del perfil <span class="req">*</span></label>
          <input type="text" id="ws-nombre" placeholder="Nombre para reconocer este perfil" value="${_escHtml(this._draft.nombre ?? "")}" />
        </div>
        <div class="wiz-form__row" data-recorrido="banco">
          <label for="ws-banco">Banco / ERP</label>
          <input type="text" id="ws-banco" placeholder="Banco o sistema contable" value="${_escHtml(this._draft.banco_o_erp ?? "")}" />
        </div>

        <p id="ws-error" class="wiz-form__error" hidden></p>
      </div>`;

    // Editando no se ofrece "usar sin guardar": el usuario vino a corregir un
    // perfil existente, y un borrador de uso único dejaría el original intacto
    // — justo lo contrario de lo que pidió.
    footer.innerHTML = `
      <button class="btn btn--ghost" id="wiz-back">${icon("back")} Atrás</button>
      ${this._esEdicion ? "" : `<button class="btn btn--secondary" id="wiz-use-once">Usar sin guardar</button>`}
      <button class="btn btn--primary" id="wiz-guardar">${icon("save")} ${
        this._esEdicion ? "Guardar cambios" : "Guardar perfil"
      }</button>`;

    footer.querySelector("#wiz-back")!.addEventListener("click", () => {
      this._paso = 2; this._render();
    });

    footer.querySelector("#wiz-use-once")?.addEventListener("click", () => {
      this._opts.onUseOnce({ ...this._draft });
      this.destroy();
    });

    footer.querySelector("#wiz-guardar")!.addEventListener("click", async () => {
      const nombre = (body.querySelector<HTMLInputElement>("#ws-nombre")?.value ?? "").trim();
      const banco  = (body.querySelector<HTMLInputElement>("#ws-banco")?.value ?? "").trim();
      const errEl  = body.querySelector<HTMLElement>("#ws-error")!;

      if (!nombre) {
        errEl.textContent = "Escriba un nombre para el perfil.";
        errEl.hidden = false;
        return;
      }

      const btn = footer.querySelector<HTMLButtonElement>("#wiz-guardar")!;
      btn.disabled    = true;
      btn.textContent = "Guardando…";

      try {
        const saved = await guardarPerfil({
          ...this._draft,
          nombre,
          banco_o_erp: banco,
          // El id convierte el POST en upsert: actualiza este perfil en vez de
          // crear uno nuevo con el mismo contenido.
          ...(this._esEdicion ? { id: this._opts.perfilExistente!.id } : {}),
        });
        this.destroy();
        this._opts.onSave(saved);
      } catch (err) {
        errEl.textContent = err instanceof ApiError ? err.message : "No fue posible guardar el perfil. Intente de nuevo.";
        errEl.hidden = false;
        btn.disabled    = false;
        btn.textContent = "Guardar perfil";
      }
    });
  }
}
